import { useMemo, useState } from "react";
import { it } from "date-fns/locale";
import { CalendarDays } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { todayISO } from "@/lib/clinic";

const toDate = (iso) => {
  const [y, m, d] = (iso || "").split("-").map(Number);
  return y ? new Date(y, m - 1, d) : new Date();
};

const toISO = (date) =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;

const DOT =
  "relative after:absolute after:bottom-1 after:left-1/2 after:h-1.5 after:w-1.5 after:-translate-x-1/2 after:rounded-full after:bg-slate-900 aria-selected:after:bg-white";

export const WorkdayPicker = ({ value, onChange, counts = {}, disabled = false, testId = "workday-picker" }) => {
  const [open, setOpen] = useState(false);
  const selected = toDate(value);

  const conPazienti = useMemo(
    () => Object.keys(counts).filter((d) => counts[d] > 0).map(toDate),
    [counts]
  );

  const seleziona = (iso) => {
    onChange(iso);
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          disabled={disabled}
          data-testid={testId}
          className="h-11 w-[200px] justify-start rounded-lg font-medium text-slate-800"
        >
          <CalendarDays className="mr-2 h-4 w-4 text-slate-500" />
          {selected.toLocaleDateString("it-IT", { day: "2-digit", month: "long", year: "numeric" })}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-auto p-0" data-testid={`${testId}-popover`}>
        <Calendar
          mode="single"
          locale={it}
          weekStartsOn={1}
          selected={selected}
          defaultMonth={selected}
          onSelect={(d) => d && seleziona(toISO(d))}
          modifiers={{ conPazienti }}
          modifiersClassNames={{ conPazienti: DOT }}
          classNames={{
            head_cell: "text-slate-500 rounded-md w-9 font-normal text-[0.8rem]",
            day: "inline-flex items-center justify-center rounded-md h-9 w-9 p-0 pb-1.5 text-sm font-normal hover:bg-slate-100 aria-selected:opacity-100",
            day_selected: "bg-slate-900 text-white hover:bg-slate-900 hover:text-white focus:bg-slate-900 focus:text-white",
            day_today: "bg-sky-50 text-sky-900 font-semibold",
          }}
        />
        <div className="flex items-center justify-between border-t border-slate-100 px-3 py-2">
          <span className="text-[11px] text-slate-500">
            <span className="mr-1.5 inline-block h-1.5 w-1.5 rounded-full bg-slate-900 align-middle" />
            giornate con pazienti
          </span>
          <button
            onClick={() => seleziona(todayISO())}
            data-testid={`${testId}-today`}
            className="text-xs font-semibold text-sky-700 hover:text-sky-900"
          >
            Oggi
          </button>
        </div>
      </PopoverContent>
    </Popover>
  );
};
